import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { TrustpilotStars } from '../components/StarRating';
import { getCompanies } from '../services/api';
import { MapPin, ExternalLink, ArrowRight } from 'lucide-react';

const CompaniesListPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const data = await getCompanies();
      setCompanies(data);
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyClick = (domain) => {
    navigate(`/review/${domain}`);
  };

  const getRatingLabel = (score) => {
    if (score >= 4.5) return 'Excellent';
    if (score >= 4) return 'Très bien';
    if (score >= 3) return 'Bien';
    if (score >= 2) return 'Médiocre';
    return 'Mauvais';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-[#1a1a1a] mb-4">
            Trouvez une entreprise de confiance
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Consultez les avis clients vérifiés et trouvez les meilleures entreprises 
            dans le secteur des stores et toiles sur mesure.
          </p>
        </div>

        {/* Stats Banner */}
        <div className="bg-white rounded-lg border p-6 mb-8">
          <div className="flex flex-wrap justify-center gap-8 text-center">
            <div>
              <p className="text-3xl font-bold text-[#00b67a]">{companies.length}</p>
              <p className="text-sm text-gray-600">Entreprises</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#00b67a]">
                {companies.reduce((acc, c) => acc + (c.stats?.total_reviews || 0), 0)}
              </p>
              <p className="text-sm text-gray-600">Avis clients</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#00b67a]">
                {companies.length > 0 
                  ? (companies.reduce((acc, c) => acc + (c.stats?.trust_score || 0), 0) / companies.length).toFixed(1)
                  : 0
                }
              </p>
              <p className="text-sm text-gray-600">Note moyenne</p>
            </div>
          </div>
        </div>

        {/* Companies List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00b67a]"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {companies.map((company) => (
              <div
                key={company.id}
                onClick={() => handleCompanyClick(company.domain)}
                className="bg-white rounded-lg border p-6 hover:shadow-lg transition-all cursor-pointer group"
              >
                {/* Company Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-full bg-[#00b67a] flex items-center justify-center text-white font-bold text-xl">
                      {company.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1a1a1a] group-hover:text-[#00b67a] transition-colors">
                        {company.name}
                      </h3>
                      <p className="text-sm text-gray-500">{company.domain}</p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-[#00b67a] group-hover:translate-x-1 transition-all" />
                </div>

                {/* Rating */}
                <div className="flex items-center gap-3 mb-4">
                  <TrustpilotStars rating={company.stats?.trust_score || 0} size="sm" />
                  <span className="font-bold text-[#1a1a1a]">{company.stats?.trust_score || 0}</span>
                  <span className="text-sm text-gray-500">
                    {getRatingLabel(company.stats?.trust_score || 0)}
                  </span>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                  <span className="font-medium">{company.stats?.total_reviews || 0} avis</span>
                  <span>•</span>
                  <span>{company.category}</span>
                </div>

                {/* Address */}
                {company.address && (
                  <div className="flex items-start gap-2 text-sm text-gray-500">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span className="line-clamp-2">{company.address}</span>
                  </div>
                )}

                {/* Rating Distribution Preview */}
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center gap-1">
                    {[5, 4, 3, 2, 1].map((star) => {
                      const percentage = company.stats?.rating_distribution?.[String(star)] || 0;
                      return (
                        <div
                          key={star}
                          className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden"
                        >
                          <div
                            className="h-full bg-[#00b67a] rounded-full"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default CompaniesListPage;
